import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from './AuthContext';
import { playNotifyDing } from '../lib/notifyAudio';
import { setFaviconDot } from '../lib/favicon';
import {
  getPermission as getDesktopPermission,
  requestPermission as requestDesktopPermissionRaw,
  showDesktopNotification,
} from '../lib/desktopNotify';

const InboxNotifyContext = createContext(null);

export const useInboxNotify = () => {
  // Optional — components outside the provider get a safe stub.
  return useContext(InboxNotifyContext) || {
    leadCount: 0, totalUnread: 0, muted: false, toggleMuted: () => {},
    desktopPermission: 'default',
    requestDesktopPermission: async () => 'default',
    refresh: () => {},
  };
};

const POLL_FOCUSED_MS = 15_000;
const POLL_HIDDEN_MS = 60_000;
const MUTE_STORAGE_KEY = 'smb_inbox_notify_muted';
const INBOX_PATH = '/smb/inbox';

const readMuted = () => {
  try { return localStorage.getItem(MUTE_STORAGE_KEY) === '1'; } catch { return false; }
};
const writeMuted = (v) => {
  try { localStorage.setItem(MUTE_STORAGE_KEY, v ? '1' : '0'); } catch { /* ignore */ }
};

export const InboxNotifyProvider = ({ children }) => {
  const { user, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const canNotify = !!user && (hasPermission('whatsapp.inbox.smb') || hasPermission('whatsapp.inbox.general') || hasPermission('smb.inbox'));

  const [leadCount, setLeadCount] = useState(0);
  const [totalUnread, setTotalUnread] = useState(0);
  const [muted, setMuted] = useState(() => readMuted());
  const [desktopPermission, setDesktopPermission] = useState(() => getDesktopPermission());

  // We track the timestamp of the latest *unread* inbound message we've
  // already alerted on. If the next poll surfaces a newer one, we ding.
  const lastAlertedAtRef = useRef(null);
  // Separately track whether the FIRST poll after page load has completed.
  // Conflating this with `lastAlertedAtRef === null` was the cause of the
  // dropped-first-message bug: if the user logged in with zero unreads,
  // `lastAlertedAtRef` stayed null forever, so when the first real inbound
  // arrived we treated it as the "first poll" and suppressed it.
  const firstPollCompletedRef = useRef(false);
  const pollTimerRef = useRef(null);
  const onInboxRef = useRef(false);

  // Keep ref in sync with the current route so the poll callback (which
  // is captured once) sees fresh values without re-subscribing.
  useEffect(() => {
    onInboxRef.current = location.pathname.startsWith(INBOX_PATH);
  }, [location.pathname]);

  const toggleMuted = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      writeMuted(next);
      return next;
    });
  }, []);

  // Lazily prompts for OS-level desktop notification permission. Must be
  // invoked from a user gesture (e.g. the header bell click) to satisfy
  // browser autoplay/notification policies.
  const requestDesktopPermission = useCallback(async () => {
    const result = await requestDesktopPermissionRaw();
    setDesktopPermission(result);
    return result;
  }, []);

  const handleSummary = useCallback((summary) => {
    const lc = summary?.lead_count || 0;
    const tu = summary?.total_unread || 0;
    setLeadCount(lc);
    setTotalUnread(tu);
    setFaviconDot(lc > 0);

    // Tab title prefix — preserve whatever DashboardLayout set as base.
    const base = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = lc > 0 ? `(${lc}) ${base}` : base;

    // Capture "first poll" status BEFORE we flip the flag. This guards
    // against the user-reported drop: when the user logs in with no
    // unreads and the very first real message arrives on poll #2, the
    // OLD logic looked at `lastAlertedAtRef === null` (still null because
    // poll #1 had nothing to set it to) and treated it as the first poll,
    // silently dropping the alert.
    const wasFirstPoll = !firstPollCompletedRef.current;
    firstPollCompletedRef.current = true;

    const latest = summary?.latest;
    const latestAt = latest?.message_at;

    // Trigger the toast + sound when we see a brand-new inbound timestamp
    // AND we're not already on the inbox page.
    if (latestAt && latestAt !== lastAlertedAtRef.current && !onInboxRef.current) {
      lastAlertedAtRef.current = latestAt;
      // Suppress only on the very first poll of this session — we don't
      // want to ding on stale unread messages from yesterday.
      if (!wasFirstPoll) {
        // Match the Inbox session list which displays business_name first.
        // Falling back to contact_name keeps individuals (no biz name) sane.
        const name = latest.business_name || latest.contact_name || 'New message';
        const preview = latest.preview || '';
        if (!muted) playNotifyDing();
        toast(name, {
          description: preview,
          duration: 6000,
          id: 'inbox-notify-toast',
          action: {
            label: 'Open',
            onClick: () => navigate(`${INBOX_PATH}?lead=${latest.lead_id}`),
          },
          className: 'inbox-notify-toast',
        });
        // OS-level desktop notification — only fires when the tab is hidden
        // (helper short-circuits otherwise) so we don't double-notify. The
        // mute toggle silences the OS sound; the visual still pops.
        showDesktopNotification({
          title: `New WhatsApp from ${name}`,
          body: preview,
          tag: 'myzow-inbox',
          silent: muted,
          onClick: () => navigate(`${INBOX_PATH}?lead=${latest.lead_id}`),
        });
      }
    }
  }, [muted, navigate]);

  const poll = useCallback(async () => {
    if (!canNotify) return;
    try {
      const res = await api.get('/smb/chat/unread-summary');
      if (res.data?.success) handleSummary(res.data.data);
    } catch {
      // Silent — a transient API hiccup shouldn't spam the user.
    }
  }, [canNotify, handleSummary]);

  // Polling loop with Page Visibility–aware cadence.
  useEffect(() => {
    if (!canNotify) {
      setLeadCount(0);
      setTotalUnread(0);
      setFaviconDot(false);
      document.title = document.title.replace(/^\(\d+\)\s*/, '');
      return undefined;
    }

    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      await poll();
      if (cancelled) return;
      const next = document.hidden ? POLL_HIDDEN_MS : POLL_FOCUSED_MS;
      pollTimerRef.current = setTimeout(tick, next);
    };

    // Run first poll immediately so the badge populates without delay.
    tick();

    const onVisibility = () => {
      // When the tab becomes visible again, poll right away to catch up.
      if (!document.hidden) {
        if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [canNotify, poll]);

  // When the user lands on the inbox page, treat the current latest message
  // as "seen" so we don't re-alert when they leave the inbox again.
  useEffect(() => {
    if (location.pathname.startsWith(INBOX_PATH)) {
      // The next poll will refresh `lastAlertedAtRef` automatically; here
      // we just immediately clear the visible badge for snappy feedback.
      // Server still owns the truth — `mark_read` calls fired by the
      // inbox page will eventually zero `unread_count`.
    }
  }, [location.pathname]);

  const value = {
    leadCount,
    totalUnread,
    muted,
    toggleMuted,
    desktopPermission,
    requestDesktopPermission,
    refresh: poll,
  };

  return (
    <InboxNotifyContext.Provider value={value}>
      {children}
    </InboxNotifyContext.Provider>
  );
};
